import { NodeEditOverlay } from '@/components/app/editor/tools/node-edit-overlay'
import { NodeEditToolbar } from '@/components/app/editor/tools/node-edit-toolbar'
import { nodeEditShapeUnchangedFromOriginal } from '../helpers/edit-mode-unchanged'
import { EditModeDimOverlay } from './edit-mode-dim-overlay'
import type {
  CanvasEditModeHandlers,
  CanvasEditModes,
  CanvasFoundation,
  CanvasViewport,
} from '../types/canvas-groups'
import type { ResolvedCenterCanvasConfig } from '../types/resolved-center-canvas-config'

/**
 * Node edit mode's chrome for the shape being path-edited: the dim overlay, the anchor/handle
 * overlay and the floating toolbar. Renders nothing while `shape` is null (not in node edit, or
 * the shape's geometry is locked/frozen).
 */
export function NodeEditModeChrome({
  config,
  foundation,
  viewport,
  editModes,
  editModeHandlers,
}: {
  config: Pick<
    ResolvedCenterCanvasConfig,
    | 'bleedWidth'
    | 'bleedHeight'
    | 'penToolDefaultPointType'
    | 'onExitPenTool'
    | 'onToggleNodeEditPenMode'
    | 'onNodeEditPointTypeChange'
  >
  foundation: Pick<CanvasFoundation, 'documentCanvasRef'>
  viewport: Pick<CanvasViewport, 'scale'>
  editModes: Pick<
    CanvasEditModes,
    | 'isPenEditMode'
    | 'nodeEditToolbarRef'
    | 'nodeEditOriginalShapeRef'
    | 'exitNodeEditMode'
    | 'setPenEditHoverKind'
  >
  editModeHandlers: Pick<
    CanvasEditModeHandlers,
    | 'nodeEditChromeShape'
    | 'nodeEditDimGeom'
    | 'handleNodeEditPathChange'
    | 'handleNodeEditPathCommit'
    | 'handleContinuePath'
    | 'handleNodeEditReset'
  >
}) {
  const {
    bleedWidth,
    bleedHeight,
    penToolDefaultPointType,
    onExitPenTool,
    onToggleNodeEditPenMode,
    onNodeEditPointTypeChange,
  } = config
  const { documentCanvasRef } = foundation
  const { scale } = viewport
  const {
    isPenEditMode,
    nodeEditToolbarRef,
    nodeEditOriginalShapeRef,
    exitNodeEditMode,
    setPenEditHoverKind: onPenHoverChange,
  } = editModes
  const {
    nodeEditChromeShape: shape,
    nodeEditDimGeom: dimGeom,
    handleNodeEditPathChange: onPathChange,
    handleNodeEditPathCommit: onPathCommit,
    handleContinuePath: onContinuePath,
    handleNodeEditReset: onReset,
  } = editModeHandlers

  if (!shape) return null

  return (
    <>
      {/* Node Edit Dim Overlay — only relevant when there's a photo clipped into the shape
          (`photoFill`) — a plain colored shape has nothing under it worth dimming the page for. */}
      {shape.photoFill && dimGeom && (
        <EditModeDimOverlay variant="node-edit" shape={shape} geom={dimGeom} />
      )}
      {/* Node Edit Overlay — shown when double-clicking a path shape */}
      <NodeEditOverlay
        shape={shape}
        bleedWidth={bleedWidth}
        bleedHeight={bleedHeight}
        scale={scale}
        onPathChange={onPathChange}
        onPathCommit={onPathCommit}
        isPenEditMode={isPenEditMode}
        onContinuePath={onContinuePath}
        onNodeAdded={undefined}
        onPenHoverChange={onPenHoverChange}
        onBackgroundDoubleClick={() => {
          const exitingShapeId = shape.id

          exitNodeEditMode(exitingShapeId)
          onExitPenTool?.()
        }}
      />
      {/* Node Edit Toolbar — floating pill above/below/beside the shape being edited */}
      <NodeEditToolbar
        ref={nodeEditToolbarRef}
        shape={shape}
        scale={scale}
        canvasRef={documentCanvasRef}
        isPenEditMode={isPenEditMode}
        onSelectMode={() => onToggleNodeEditPenMode?.(false)}
        onPenMode={() => onToggleNodeEditPenMode?.(true)}
        onReset={onReset}
        resetDisabled={nodeEditShapeUnchangedFromOriginal(shape, nodeEditOriginalShapeRef.current)}
        pointType={penToolDefaultPointType}
        onPointTypeChange={(type) => onNodeEditPointTypeChange?.(type)}
      />
    </>
  )
}
